"use client";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown2 } from "iconsax-reactjs";
import { Check } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ComponentPropsWithoutRef } from "react";
import {
  Controller,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { XIcon } from "@/components/ui/XIcon";
import { cn } from "@/lib/utils";
import { Spinner } from "../spinner";

// ----------------------------------------------------------------------

type City = {
  name: string;
  latitude: string;
  longitude: string;
};

type CityOption = {
  label: string;
  value: {
    name: string;
    latitude: string;
    longitude: string;
  };
};

interface CustomRHFCitySelectProps<TFieldValues extends FieldValues>
  extends Omit<
    ComponentPropsWithoutRef<typeof Controller>,
    "name" | "control" | "render"
  > {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: string;
  placeholder?: string;
  emptyText?: string;
  multiple?: boolean;
  selectContentClassName?: string;
  selectItemClassName?: string;
  triggerClassName?: string;
  loading?: boolean;
}

async function fetchCities(): Promise<City[]> {
  const response = await fetch(
    "https://www.iran-locations-api.ir/api/v1/fa/cities",
  );
  if (!response.ok) {
    throw new Error("Failed to fetch cities");
  }
  return response.json();
}

export default function CustomRHFCitySelect<TFieldValues extends FieldValues>({
  name,
  helperText,
  defaultValue,
  rules,
  containerClass,
  placeholder,
  emptyText,
  multiple = false,
  selectContentClassName,
  selectItemClassName,
  triggerClassName,
  loading: externalLoading,
  ...other
}: CustomRHFCitySelectProps<TFieldValues>) {
  const { control } = useFormContext();
  const t = useTranslations("common.form");
  const { data: cities, isLoading } = useQuery({
    queryKey: ["iran-cities"],
    queryFn: fetchCities,
    staleTime: 1000 * 60 * 60 * 24, // Cache for 24 hours
  });

  const options: CityOption[] =
    cities
      ?.filter((city, index, self) => {
        // Filter duplicates by city name, keep first occurrence
        return index === self.findIndex((c) => c.name === city.name);
      })
      .map((city) => ({
        label: city.name,
        value: {
          name: city.name,
          latitude: city.latitude,
          longitude: city.longitude,
        },
      })) || [];

  const loading = isLoading || externalLoading;

  if (multiple) {
    return (
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue || []}
        rules={rules}
        render={({ field, fieldState: { error } }) => {
          const selectedValues = (field.value || []) as Array<{
            name: string;
            latitude: string;
            longitude: string;
          }>;

          // Helper to check if a city is selected (by name)
          const isCitySelected = (city: CityOption["value"]) => {
            return selectedValues.some(
              (selected) => selected.name === city.name,
            );
          };

          const handleToggle = (option: CityOption) => {
            const isSelected = isCitySelected(option.value);

            if (isSelected) {
              // Remove the city
              const newValues = selectedValues.filter(
                (item) => item.name !== option.value.name,
              );
              field.onChange(newValues);
            } else {
              // Add the city
              field.onChange([...selectedValues, option.value]);
            }
          };

          const handleRemoveBadge = (
            e: React.MouseEvent,
            cityToRemove: CityOption["value"],
          ) => {
            e.stopPropagation();
            const newValues = selectedValues.filter(
              (item) => item.name !== cityToRemove.name,
            );
            field.onChange(newValues);
          };

          return (
            <div className={cn("space-y-2 w-full", containerClass)}>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="bordered"
                    colors="secondary"
                    disabled={loading}
                    radius="xs"
                    className={cn(
                      "w-full justify-between h-9 border-stroke-light bg-transparent px-2",
                      !selectedValues.length && "text-muted-foreground",
                      error && "border-semantic-error",
                      triggerClassName,
                    )}
                  >
                    {loading ? (
                      <Spinner className="h-4 w-4 text-text-secondary ms-auto" />
                    ) : (
                      <>
                        <span className="truncate line-clamp-1 text-muted-foreground">
                          {selectedValues.length > 0
                            ? t("selectedCount", {
                                count: selectedValues.length,
                              })
                            : placeholder || emptyText || t("selectCity")}
                        </span>
                        <ArrowDown2 size={16} className="text-primary" />
                      </>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-(--radix-popover-trigger-width) p-0 bg-bg-secondary rounded-md border-stroke-subtle shadow-[0px_2px_4px_-2px_#0000001A,0px_4px_6px_-1px_#0000001A]"
                  align="end"
                >
                  <div className="max-h-60 overflow-auto p-2">
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <Spinner className="h-5 w-5 text-text-secondary" />
                      </div>
                    ) : options.length === 0 ? (
                      <div className="flex items-center justify-center py-8">
                        <span className="text-sm text-text-secondary">
                          {emptyText || t("noCityFound")}
                        </span>
                      </div>
                    ) : (
                      options.map((option) => {
                        const isSelected = isCitySelected(option.value);
                        return (
                          <div
                            key={`${option.value.latitude},${option.value.longitude}`}
                            className={cn(
                              "flex items-center gap-2 rounded-xs px-2 py-1.5 hover:bg-bg-tertiary/60 cursor-pointer",
                            )}
                            onClick={() => handleToggle(option)}
                          >
                            {isSelected ? (
                              <Check className="h-4 w-4 text-primary" />
                            ) : (
                              <span className="h-4 w-4" />
                            )}
                            <span className="text-sm font-medium flex-1">
                              {option.label}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              {selectedValues.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedValues.map((city) => (
                    <Badge
                      key={`${city.latitude},${city.longitude}`}
                      variant="outline"
                      size="sm"
                      className="gap-1.5 pr-1 pl-1 py-0.5 text-xs bg-transparent"
                    >
                      <span className="truncate max-w-[100px] mt-0.5">
                        {city.name}
                      </span>
                      <button
                        type="button"
                        className="flex items-center justify-center hover:bg-bg-tertiary rounded-xs p-0.5 transition-colors"
                        onClick={(e) => handleRemoveBadge(e, city)}
                      >
                        <XIcon size={16} className="text-text-secondary" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {!!helperText && (
                <p className="text-xs leading-tight text-muted-foreground ml-1.5">
                  {helperText}
                </p>
              )}
              {!!error && (
                <p className="text-xs leading-tight text-semantic-error ml-1.5">
                  {error?.message}
                </p>
              )}
            </div>
          );
        }}
        {...other}
      />
    );
  }

  // For single select, use Controller directly with Select component
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        // Transform the field value (object) to string for display
        const stringValue =
          field.value && typeof field.value === "object"
            ? `${field.value.latitude},${field.value.longitude}`
            : "";

        const handleValueChange = (value: string) => {
          // Transform string value back to object
          const selectedCity = options.find(
            (opt) => `${opt.value.latitude},${opt.value.longitude}` === value,
          );
          if (selectedCity) {
            field.onChange(selectedCity.value);
          } else {
            field.onChange(undefined);
          }
        };

        return (
          <div className={cn("space-y-2 w-full", containerClass)}>
            <Select
              onValueChange={handleValueChange}
              value={stringValue}
              disabled={loading}
            >
              <SelectTrigger
                className={cn(
                  "w-full",
                  error && "border-semantic-error",
                  triggerClassName,
                )}
                size="default"
                aria-invalid={error ? true : undefined}
              >
                {loading ? (
                  <Spinner className="h-4 w-4 text-text-secondary ms-auto" />
                ) : (
                  <SelectValue
                    placeholder={placeholder || emptyText || t("select")}
                  />
                )}
              </SelectTrigger>
              <SelectContent
                className={cn(
                  "bg-bg-secondary rounded-sm border-stroke-subtle shadow-[0px_2px_4px_-2px_#0000001A,0px_4px_6px_-1px_#0000001A]",
                  selectContentClassName,
                )}
              >
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner className="h-5 w-5 text-text-secondary" />
                  </div>
                ) : (
                  <>
                    {emptyText && (
                      <SelectItem
                        value="empty"
                        className={cn(
                          "rounded-sm focus:bg-bg-tertiary/60 outline-none",
                          selectItemClassName,
                        )}
                      >
                        <span className="text-primary">{emptyText}</span>
                      </SelectItem>
                    )}
                    {options.map((opt) => {
                      const optValue = `${opt.value.latitude},${opt.value.longitude}`;
                      return (
                        <SelectItem
                          key={optValue}
                          value={optValue}
                          className={cn(
                            "font-medium rounded-sm",
                            selectItemClassName,
                          )}
                        >
                          {opt.label}
                        </SelectItem>
                      );
                    })}
                  </>
                )}
              </SelectContent>
            </Select>
            {!!helperText && (
              <p className="text-xs leading-tight text-muted-foreground ml-1.5">
                {helperText}
              </p>
            )}
            {!!error && (
              <p className="text-xs leading-tight text-semantic-error ml-1.5">
                {error?.message}
              </p>
            )}
          </div>
        );
      }}
      {...other}
    />
  );
}
